import React, { useEffect, useRef, useState } from 'react';
import { ComboBox, Content, Dialog, Flex, Footer, Header, Item, Link, TextArea, TextField, View } from '@adobe/react-spectrum';
import { ArrowLeft } from 'react-bootstrap-icons';
import { useAsyncList } from 'react-stately';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../../../hooks/UseViewInfo';
import { usePrevious } from '@uidotdev/usehooks';
import { ToastQueue } from '@adobe/react-spectrum';
import { useDebounce } from '@uidotdev/usehooks';
import { ListCountryRequest } from '../../../../../services/soap/translate/reqeusts/ListCountryRequest';
import { ListLanguagesRequest } from '../../../../../services/soap/translate/reqeusts/ListLanguagesRequest';
import { CreateContactRequest } from '../../../../../services/soap/main/requests/CreateContactRequest';
import { UpdateActivityRequest } from '../../../../../services/soap/project/requests/UpdateActivityRequest';
import { UpdateProjectRequest } from '../../../../../services/soap/project/requests/UpdateProjectRequest';
import { UpdateFolderRequest } from '../../../../../services/soap/project/requests/UpdateFolderRequest';
import { GetContactListRequest } from '../../../../../services/soap/main/requests/GetContactListRequest';
import { GetContactRequest } from '../../../../../services/soap/main/requests/GetContactRequest';
import styles from './ExternalNewContacts.module.css';

interface ExternalNewContactProps {
  /** Default `fpa`: link new contact to project/activity/folder external contacts. */
  variant?: 'fpa' | 'standalone';
  selectedItem?: any;
  currentItem?: any;
  FPADataTypes?: any;
  updateItem?: (request: any, message: string) => void;
  openDialog?: (isOpen: boolean) => void;
  closeDialog: (reload: boolean) => void;
  /** When `variant` is `standalone`, pre-fill email from message sender. */
  prefillEmail?: string;
  prefilledSenderName?: string;
  onStandaloneSuccess?: (contactId: string) => void;
}

type formDataType = {
  contactId: string;
  name: string;
  nameDisable: boolean;
  surname: string;
  surnameSearchValue: string;
  surnameRequired: boolean;
  surnameDisable: boolean;
  phone: string;
  phoneDisable: boolean;
  countryId: string;
  countryIdDisable: boolean;
  email: string;
  emailSearchValue: string;
  emailRequired: boolean;
  emailDisable: boolean;
  languageId: string;
  languageIdDisable: boolean;
  description: string;
};

const ExternalNewContact: React.FC<ExternalNewContactProps> = ({
  variant = 'fpa',
  selectedItem,
  currentItem,
  FPADataTypes,
  updateItem,
  openDialog,
  closeDialog,
  prefillEmail,
  prefilledSenderName,
  onStandaloneSuccess,
}) => {
  const isStandalone = variant === 'standalone';
  const { isMobile } = useViewInfo();
  const { t } = useTranslation();
  const { translateService, mainService, projectService, store } = useDependency();
  const [countryList, setCountryList] = useState<any>([]);
  const [langList, setLangList] = useState<any>([]);
  const [formValue, setFormValue] = useState<formDataType>({
    contactId: '',
    name: '',
    nameDisable: false,
    surname: '',
    surnameSearchValue: '',
    surnameRequired: true,
    surnameDisable: false,
    phone: '',
    phoneDisable: false,
    countryId: '',
    countryIdDisable: false,
    email: '',
    emailSearchValue: '',
    emailRequired: true,
    emailDisable: false,
    languageId: '131427101',
    languageIdDisable: false,
    description: '',
  });
  const descRef = useRef<any>(null);
  const debouncedSurNameFilterTerm = useDebounce(formValue.surnameSearchValue, 2000);
  const debouncedEmailFilterTerm = useDebounce(formValue.emailSearchValue, 2000);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchResultsEmail, setSearchResultsEmail] = useState<any[]>([]);
  const previousSearchResults = usePrevious(searchResults);

  useEffect(() => {
    (async () => {
      await loadDropDownData();
    })();
  }, []);

  useEffect(() => {
    listSurName.setFilterText(debouncedSurNameFilterTerm);
  }, [debouncedSurNameFilterTerm]);

  useEffect(() => {
    listEmail.setFilterText(debouncedEmailFilterTerm);
  }, [debouncedEmailFilterTerm]);

  const loadDropDownData = async () => {
    const [countryData, langData] = await Promise.all([
      translateService.listCountry(new ListCountryRequest(store.Server, store.SessionId, {})),
      translateService.listLanguages(new ListLanguagesRequest(store.Server, store.SessionId)),
    ]);

    setCountryList(countryData.ROWS?.ROW);
    setLangList(langData.LANGUAGE);
  };

  const BackContactButton = () => (
    <Flex UNSAFE_className={styles.button_container_new_contact_second}>
      <ArrowLeft
        onClick={() => openDialog?.(true)}
        fontSize={20}
        style={{ cursor: 'pointer' }}
      />
      <Link isQuiet UNSAFE_className={styles.back_text} onPress={() => openDialog?.(true)}>
        {t('back', { ns: 'layout_components' })}
      </Link>
    </Flex>
  );

  const ContactActions = () => (
    <Flex UNSAFE_className={styles.button_container_new_contact}>
      {formValue.contactId && (
        <Flex UNSAFE_className={styles.new_contact_clear}>
          <Link isQuiet UNSAFE_className={styles.new_contact_clear_text} onPress={() => enableDisableFields('', '')}>
            {t('clear', { ns: 'layout_components' })}
          </Link>
        </Flex>
      )}
      <Flex UNSAFE_className={styles.new_contact_cancel}>
        <Link isQuiet UNSAFE_className={styles.new_contact_cancel_text} onPress={() => closeDialog(false)}>
          {t('cancel', { ns: 'layout_components' })}
        </Link>
      </Flex>

      <Flex UNSAFE_className={styles.new_contact_add}>
        <Link isQuiet UNSAFE_className={styles.new_contact_add_text} onPress={validateAndSubmit}>
          {t('add_contact', { ns: 'layout_components' })}
        </Link>
      </Flex>
    </Flex>
  );

  const ContactButtons = () => (
    <Flex direction={isMobile ? 'column' : { base: 'column', L: 'row' }} justifyContent={'end'} gap={'size-100'}>
      {!isStandalone && <BackContactButton />}
      <ContactActions />
    </Flex>
  );

  const createContactData = () => {
    const CONTACTS = [];
    if (formValue.email || formValue.emailSearchValue) {
      CONTACTS.push({ content: formValue.email || formValue.emailSearchValue, description: '', isMain: 1, isPrimary: 1, type: 132026101, typeName: 'E-mail', typeType: 1 });
    }
    if (formValue.phone) {
      CONTACTS.push({ content: formValue.phone, description: '', isMain: 0, isPrimary: 1, type: 132030101, typeName: 'Telefon', typeType: 2 });
    }
    //console.log('formValue.surname', formValue);
    const contactData: any = {
      name3: formValue.name,
      name1: formValue.surname || formValue.surnameSearchValue,
      PERSONINFO: { preferedLang: formValue.languageId },
      description: formValue.description,
    };

    if (formValue.countryId) {
      contactData.ADDRESSES = { ADDRESS: [{ country: formValue.countryId, isPrimary: 1, type: 132022101 }] };
    }

    if (CONTACTS.length > 0) {
      contactData.CONTACTS = { CONTACT: CONTACTS };
    }

    return contactData;
  };

  const updateExternalContacts = (contactId: string) => {
    const externalContact = {
      contactId,
      note: formValue.description,
      name1: formValue.surname || formValue.surnameSearchValue,
      name2: '',
      name3: formValue.name,
      type: 1,
      email: formValue.email || formValue.emailSearchValue,
      phone: formValue.phone,
      mobile: '',
      role: '',
      deleteContact: 0,
    };

    const normalizeExistingContacts = () => {
      const externalContacts = currentItem.current?.EXTERNAL_CONTACTS;
      if (!externalContacts) return [];

      if (selectedItem?.type === FPADataTypes.ACTIVITY) {
        const rowsSource = externalContacts?.ROWS;
        const rows = Array.isArray(rowsSource)
          ? rowsSource
          : rowsSource?.EXTERNAL_CONTACT
            ? [{ EXTERNAL_CONTACT: rowsSource.EXTERNAL_CONTACT }]
            : [];

        return rows
          .map((row: any) => row?.EXTERNAL_CONTACT)
          .flat()
          .filter(Boolean);
      }

      if (Array.isArray(externalContacts?.EXTERNAL_CONTACT)) {
        return externalContacts.EXTERNAL_CONTACT.filter(Boolean);
      }

      if (externalContacts?.ROWS?.EXTERNAL_CONTACT) {
        return [externalContacts.ROWS.EXTERNAL_CONTACT].flat().filter(Boolean);
      }

      return [];
    };

    const nextContacts = [...normalizeExistingContacts(), externalContact].filter(
      (contact: any, index: number, arr: any[]) =>
        index ===
        arr.findIndex((entry: any) =>
          String(entry?.id || '') === String(contact?.id || '') ||
          (String(entry?.contactId || '') !== '' &&
            String(entry?.contactId || '') === String(contact?.contactId || ''))
        )
    );

    if (selectedItem?.type === FPADataTypes.ACTIVITY) {
      return {
        count: String(nextContacts.length),
        ROWS: nextContacts.map((contact: any) => ({
          EXTERNAL_CONTACT: contact,
        })),
      };
    }

    return {
      count: String(nextContacts.length),
      EXTERNAL_CONTACT: nextContacts,
    };
  };

  const validateAndSubmit = async () => {
    if (isStandalone) {
      if (formValue.contactId) {
        const id = String(formValue.contactId);
        ToastQueue.positive(
          t('contact_selected', {
            ns: 'layout_components',
            defaultValue: 'Existing contact selected.',
          }),
          { timeout: 2500 },
        );
        onStandaloneSuccess?.(id);
        closeDialog(true);
        return;
      }
      const contactData = createContactData();
      const contactResp = await mainService.createContact(
        new CreateContactRequest(store.Server, store.SessionId, contactData),
      );

      if (contactResp.result !== 'OK') {
        ToastQueue.negative(contactResp.EXCEPTION?.message ?? t('save_failed', { ns: 'layout_components' }), {
          timeout: 3000,
        });
        return;
      }
      ToastQueue.positive(
        t('contact_created_ok', {
          ns: 'layout_components',
          defaultValue: 'Contact created successfully.',
        }),
        { timeout: 3000 },
      );
      onStandaloneSuccess?.(String(contactResp.newId));
      closeDialog(true);
      return;
    }

    let contactResp: any;
    if (!formValue.contactId) {
      const contactData = createContactData();
      contactResp = await mainService.createContact(new CreateContactRequest(store.Server, store.SessionId, contactData));

      if (contactResp.result !== 'OK') {
        ToastQueue.negative(contactResp.EXCEPTION.message, { timeout: 3000 });
        return;
      }
    }

    const requestData = {
      server: store.Server,
      sessionId: store.SessionId,
      data: {
        ...currentItem!.current,
        EXTERNAL_CONTACTS: updateExternalContacts(formValue.contactId || contactResp.newId),
      },
    };

    const updateFunctions = {
      [FPADataTypes!.ACTIVITY]: () => projectService.updateActivity(new UpdateActivityRequest(requestData.server, requestData.sessionId, requestData.data)),
      [FPADataTypes!.PROJECT]: () => projectService.updateProject(new UpdateProjectRequest(requestData.server, requestData.sessionId, requestData.data)),
      [FPADataTypes!.FOLDER]: () => projectService.updateFolder(new UpdateFolderRequest(requestData.server, requestData.sessionId, requestData.data)),
    };

    updateItem!(updateFunctions[selectedItem!.type], 'Contact added successfully');
    closeDialog(true);
  };

  let listSurName = useAsyncList({
    async load({ filterText }) {
      let newResultArray: { [key: string]: Object }[] = [];

      if (filterText === '') {
        return {
          items: newResultArray,
          cursor: undefined,
        };
      }
      //console.log(' listSurName filterText', filterText);

      let result: any = await mainService.getContactList(
        new GetContactListRequest(store.Server, store.SessionId, {
          offset: 0,
          limit: 20,
          contains: '*' + filterText,
        })
      );

      //console.log('result', result);
      if (result?.count > 0) {
        //console.log(result.ITEMS.some((item: any) => 'FOLDER' in item?.ITEM));
        result.CONTACT?.forEach((contact: any) => {
          //console.log('contact', contact);
          newResultArray.push({
            ...contact,
            id: contact?.id,
            name: `${contact?.name3} ${contact?.name1}`,
          });
        });
      } else {
        newResultArray = previousSearchResults || [];
      }

      //console.log(newResultArray);
      setSearchResults(newResultArray);
      return {
        items: newResultArray,
        cursor: undefined,
      };
    },
  });

  let listEmail = useAsyncList({
    async load({ filterText }) {
      let newResultArray: { [key: string]: Object }[] = [];
      if (filterText === '') {
        return {
          items: newResultArray,
          cursor: undefined,
        };
      }

      //console.log('debouncedEmailFilterTerm', debouncedEmailFilterTerm);
      //console.log('listEmail filterText', debouncedEmailFilterTerm);
      let result: any = await mainService.getContactList(
        new GetContactListRequest(store.Server, store.SessionId, {
          offset: 0,
          limit: 20,
          contactContact: filterText,
        })
      );

      //console.log('result', result);
      if (result?.count > 0) {
        //console.log(result.ITEMS.some((item: any) => 'FOLDER' in item?.ITEM));
        result.CONTACT?.forEach((contact: any) => {
          //console.log('contact', contact);
          newResultArray.push({
            ...contact,
            id: contact?.id,
            name: `${contact?.name3} ${contact?.name1}`,
          });
        });
      } else {
        newResultArray = previousSearchResults || [];
      }

      //console.log(newResultArray);
      setSearchResultsEmail(newResultArray);
      return {
        items: newResultArray,
        cursor: undefined,
      };
    },
  });

  const standalonePrefillAppliedRef = useRef(false);
  useEffect(() => {
    if (!isStandalone || standalonePrefillAppliedRef.current) return;
    const email = prefillEmail?.trim();
    const name = prefilledSenderName?.trim();
    if (!email && !name) return;
    standalonePrefillAppliedRef.current = true;
    setFormValue((prev) => ({
      ...prev,
      ...(email
        ? {
            email,
            emailSearchValue: email,
            emailRequired: false,
          }
        : {}),
      ...(name ? { name } : {}),
    }));
    if (email) {
      listEmail.setFilterText(email);
    }
  }, [isStandalone, prefillEmail, prefilledSenderName]);

  async function enableDisableFields(selectedValue: any, selectedControl: string) {
    console.log('enableDisableFields', selectedValue);
    if (!selectedValue) {
      listEmail.setFilterText('');
      listSurName.setFilterText('');
      setSearchResults([]);
      setSearchResultsEmail([]);
      setFormValue(prev => ({
        ...prev,
        contactId: '',
        name: '',
        nameDisable: false,
        surname: '',
        surnameSearchValue: '',
        surnameRequired: true,
        surnameDisable: false,
        phone: '',
        phoneDisable: false,
        email: '',
        emailSearchValue: '',
        emailRequired: true,
        emailDisable: false,
        countryId: '',
        countryIdDisable: false,
        languageId: '131427101',
        languageIdDisable: false,
        description: '',
      }));
      return;
    }

    let contactData = selectedControl === 'surname' ? searchResults.find(item => item.name === selectedValue) : searchResultsEmail.find(item => item.email === selectedValue);

    //console.log('contactData', contactData);

    if (selectedControl === 'surname') {
      listEmail.setFilterText(contactData?.email || '');
    } else {
      listSurName.setFilterText(contactData?.name || '');
    }

    if (!contactData?.id) return;

    const contactDetail = await mainService.getContact(new GetContactRequest(store.Server, store.SessionId, contactData.id, undefined, contactData.id));

    setFormValue((prev: any) => ({
      ...prev,
      contactId: contactData?.id,
      surname: selectedControl === 'surname' ? selectedValue : contactData?.name,
      surnameDisable: selectedControl === 'surname' ? false : true,
      surnameRequired: false,
      name: contactData?.name3,
      nameDisable: true,
      phone: contactData?.phone,
      phoneDisable: true,
      countryId: countryList.find((country: any) => country.name === contactData?.country)?.countryId,
      countryIdDisable: true,
      languageId: contactDetail?.PERSON?.PERSONINFO?.preferedLang,
      languageIdDisable: true,
      email: selectedControl === 'email' ? selectedValue : contactData?.email,
      emailRequired: false,
      emailDisable: selectedControl === 'surname' ? true : false,
    }));
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      validateAndSubmit();
    }
  };

  const handleSurnameSelectionChange = (selectedKey: any) => {
    //console.log('handleSurnameSelectionChange selectedKey', selectedKey);
    const selectedUser: any = listSurName.items.find((user: any) => user.name == selectedKey);
    if (selectedUser) {
      setFormValue(prev => ({
        ...prev,
        surnameSearchValue: selectedUser.name,
        surnameRequired: false,
        surname: selectedUser.name,
      }));
      descRef.current?.focus();
      enableDisableFields(selectedUser.name, 'surname');
    }
  };

  const handleEmailSelectionChange = (selectedKey: any) => {
    //console.log('handleEmailSelectionChange selectedKey', selectedKey);
    const selectedEmail: any = listEmail.items.find((rec: any) => rec.email == selectedKey);
    if (selectedEmail) {
      setFormValue(prev => ({
        ...prev,
        emailSearchValue: selectedEmail.email,
        email: selectedEmail.email,
        emailRequired: false,
      }));
      descRef.current?.focus();
      enableDisableFields(selectedEmail.email, 'email');
    }
  };

  return (
    <Dialog>
      <Header UNSAFE_className={styles.new_contact_heading}>{t('new_contact', { ns: 'layout_components' })}</Header>
      <Content height={'350px'}>
        <div style={{ width: '100%' }} onKeyUp={handleKeyUp}>
          <Flex direction="column" width={'100%'} justifyContent={'center'} alignContent={'center'}>
            <View UNSAFE_className={styles.add_contact_parent}>
              {/* <View UNSAFE_className={styles.add_client_contact_heading}>New Contact</View> */}
              <Flex UNSAFE_className={styles.form_parent}>
                <Flex UNSAFE_className={styles.form_group}>
                  {isMobile ? (
                    <table width={'100%'}>
                      <thead>
                        <tr>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('name', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>

                              <TextField
                                value={formValue.name}
                                isDisabled={formValue.nameDisable}
                                width={'100%'}
                                onChange={e => {
                                  setFormValue((prevValue: any) => ({ ...prevValue, name: e }));
                                }}
                              ></TextField>
                            </Flex>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('surname', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>
                              <ComboBox
                                id="cmbSurNameMobile"
                                isDisabled={formValue.surnameDisable}
                                width={'100%'}
                                selectedKey={formValue.surname}
                                inputValue={formValue.surnameSearchValue || listSurName.filterText}
                                onInputChange={value => {
                                  setFormValue(prev => ({
                                    ...prev,
                                    surnameSearchValue: value,
                                    surnameRequired: false,
                                  }));
                                }}
                                onSelectionChange={handleSurnameSelectionChange}
                                validationState={formValue.surnameRequired ? 'invalid' : 'valid'}
                                items={listSurName.items}
                              >
                                {(item: any) => <Item key={item.name}>{item.name}</Item>}
                              </ComboBox>
                            </Flex>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('phone', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>
                              <TextField
                                type="number"
                                value={formValue.phone}
                                isDisabled={formValue.phoneDisable}
                                width={'100%'}
                                onChange={e => {
                                  setFormValue((prevValue: any) => ({ ...prevValue, phone: e }));
                                }}
                              ></TextField>
                            </Flex>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <View UNSAFE_className={styles.form_label_text}>{t('country', { ns: 'layout_components' })}</View>
                              </Flex>
                              <ComboBox
                                defaultSelectedKey={formValue.countryId}
                                selectedKey={formValue.countryId}
                                isDisabled={formValue.countryIdDisable}
                                width={'100%'}
                                label=""
                                defaultItems={countryList}
                                onSelectionChange={value => {
                                  if (value !== null) {
                                    setFormValue((prevValue: any) => ({ ...prevValue, countryId: value }));
                                  }
                                }}
                              >
                                {(item: any) => <Item key={item.countryId}>{item.countryName}</Item>}
                              </ComboBox>
                            </Flex>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('email', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>
                              <ComboBox
                                id="cmbEmailMobile"
                                isDisabled={formValue.emailDisable}
                                width={'100%'}
                                selectedKey={formValue.email}
                                inputValue={formValue.emailSearchValue || listEmail.filterText}
                                onInputChange={value => {
                                  setFormValue(prev => ({
                                    ...prev,
                                    emailSearchValue: value,
                                    emailRequired: false,
                                  }));
                                }}
                                validationState={formValue.emailRequired ? 'invalid' : 'valid'}
                                items={listEmail.items}
                                onSelectionChange={handleEmailSelectionChange}
                              >
                                {(item: any) => <Item key={item.email}>{item.email}</Item>}
                              </ComboBox>
                            </Flex>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('language', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>
                              <ComboBox
                                defaultInputValue={formValue.languageId}
                                isDisabled={formValue.languageIdDisable}
                                width={'100%'}
                                label=""
                                defaultItems={langList}
                                selectedKey={formValue.languageId}
                                onSelectionChange={value => {
                                  if (value !== null) {
                                    setFormValue((prevValue: any) => ({ ...prevValue, languageId: value }));
                                  }
                                }}
                              >
                                {(item: any) => <Item key={item?.id}>{item?.languageLong}</Item>}
                              </ComboBox>
                            </Flex>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <table width={'100%'}>
                      <thead>
                        <tr>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('name', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>

                              <TextField
                                autoFocus
                                autoComplete="false"
                                value={formValue.name}
                                isDisabled={formValue.nameDisable}
                                width={'100%'}
                                onChange={e => {
                                  setFormValue((prevValue: any) => ({ ...prevValue, name: e }));
                                }}
                              ></TextField>
                            </Flex>
                          </td>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('surname', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>
                              <ComboBox
                                id="cmbSurName"
                                isDisabled={formValue.surnameDisable}
                                width={'100%'}
                                selectedKey={formValue.surname}
                                inputValue={formValue.surnameSearchValue || listSurName.filterText}
                                onInputChange={value => {
                                  setFormValue(prev => ({
                                    ...prev,
                                    surnameSearchValue: value,
                                    surnameRequired: false,
                                  }));
                                }}
                                onSelectionChange={handleSurnameSelectionChange}
                                validationState={formValue.surnameRequired ? 'invalid' : 'valid'}
                                items={listSurName.items}
                              >
                                {(item: any) => <Item key={item.name}>{item.name}</Item>}
                              </ComboBox>
                            </Flex>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('phone', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>
                              <TextField
                                type="number"
                                value={formValue.phone}
                                isDisabled={formValue.phoneDisable}
                                width={'100%'}
                                onChange={e => {
                                  setFormValue((prevValue: any) => ({ ...prevValue, phone: e }));
                                }}
                              ></TextField>
                            </Flex>
                          </td>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('country', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>
                              <ComboBox
                                defaultSelectedKey={formValue.countryId}
                                selectedKey={formValue.countryId}
                                isDisabled={formValue.countryIdDisable}
                                width={'100%'}
                                label=""
                                defaultItems={countryList}
                                onSelectionChange={value => {
                                  if (value !== null) {
                                    setFormValue((prevValue: any) => ({ ...prevValue, countryId: value }));
                                  }
                                }}
                              >
                                {(item: any) => <Item key={item.countryId}>{item.countryName}</Item>}
                              </ComboBox>
                            </Flex>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('email', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>
                              <ComboBox
                                id="cmbEmail"
                                isDisabled={formValue.emailDisable}
                                width={'100%'}
                                selectedKey={formValue.email}
                                inputValue={formValue.emailSearchValue || listEmail.filterText}
                                onInputChange={value => {
                                  setFormValue(prev => ({
                                    ...prev,
                                    emailSearchValue: value,
                                    emailRequired: false,
                                  }));
                                }}
                                validationState={formValue.emailRequired ? 'invalid' : 'valid'}
                                items={listEmail.items}
                                onSelectionChange={handleEmailSelectionChange}
                              >
                                {(item: any) => <Item key={item.email}>{item.email}</Item>}
                              </ComboBox>
                            </Flex>
                          </td>
                          <td>
                            {' '}
                            <Flex UNSAFE_className={styles.form_input}>
                              <Flex UNSAFE_className={styles.form_label_container}>
                                <Flex UNSAFE_className={styles.form_label}>
                                  <View UNSAFE_className={styles.form_label_text}>{t('language', { ns: 'layout_components' })}</View>
                                </Flex>
                              </Flex>
                              <ComboBox
                                defaultInputValue={formValue.languageId}
                                isDisabled={formValue.languageIdDisable}
                                width={'100%'}
                                label=""
                                defaultItems={langList}
                                selectedKey={formValue.languageId}
                                onSelectionChange={value => {
                                  if (value !== null) {
                                    setFormValue((prevValue: any) => ({ ...prevValue, languageId: value }));
                                  }
                                }}
                              >
                                {(item: any) => <Item key={item?.id}>{item?.languageLong}</Item>}
                              </ComboBox>
                            </Flex>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </Flex>
                <Flex UNSAFE_className={styles.form_input_large}>
                  <Flex UNSAFE_className={styles.form_label_container}>
                    <Flex UNSAFE_className={styles.form_label}>
                      <View UNSAFE_className={styles.form_label_text}>{t('description', { ns: 'layout_components' })}</View>
                    </Flex>
                  </Flex>

                  <TextArea
                    ref={descRef}
                    width={isMobile ? '100%' : '95%'}
                    onChange={e => {
                      setFormValue((prevValue: any) => ({ ...prevValue, description: e }));
                    }}
                  ></TextArea>
                </Flex>
              </Flex>
            </View>
          </Flex>
        </div>
      </Content>
      <Footer>
        <View UNSAFE_className={styles.footer}>
          <ContactButtons />
        </View>
      </Footer>
    </Dialog>
  );
};

export default ExternalNewContact;
